import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Switch,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import {
  addButtonPadding,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import PatientResistrationForm from "./PatientRegistration";
import Grid from "@mui/material/Grid";
import SlotassignDialog from "./SlotManagement/SlotassignDialog";
import { DataGrid } from "@mui/x-data-grid";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import validators from "../../utils/validations/validators";
import { roleFlags } from "../../shared/helper";
import api from "../../services/api";
import PatientSessionModal from "./PatientSessionsModal";

const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#059669",
  tealSoft: "#ECFDF5",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};

const actionIconBtn = (color, hoverBg) => ({
  width: 30,
  height: 30,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color,
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: hoverBg },
});

export default function PatientTable(props) {
  const { branchId, getPatientDetails, rows, userDetail } = props;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [patientId, setPatientId] = useState();
  const [serviceDetails, setServicesDetails] = useState();
  const [therapyPlansData, settherapyPlansData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState();
  const [formData, setFormData] = useState({
    user: {
      address: "",
      email: "",
      name: "",
      phone_number: "",
    },
    patient: {
      patient_name: "",
      date_of_birth: null,
      gender: "",
    },
  });
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [patientSlotData, setPatientSlotData] = useState({});
  const [slotSubmitLoading, setSlotSubmitLoading] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const APP_URL = process.env.REACT_APP_URL;

  const DEFAULT_IMAGE =
    BASE_URL + "/static/profile_pic/default_profile_pic.png";
  const flags = roleFlags(userDetail?.role);

  /* ================================================================== */
  /* UI / dialog visibility handlers                                     */
  /* ================================================================== */

  const handleSnackClose = () => setSnackbarOpen(false);
  const handleOpen = () => setOpen(true);

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    if (reason === "escapeKeyDown") return;

    setFormData({
      user: {
        address: "",
        email: "",
        name: "",
        phone_number: "",
      },
      patient: {
        patient_name: "",
        date_of_birth: null,
        gender: "",
        profile_pic_path: "",
      },
    });
    setIsEditClicked(false);
    setValidationErrors({});
    setOpen(false);
  };

  const handleClosePatientModal = () => {
    setOpenPatientModal(false);
  };

  const handleNameClick = (row) => {
    setOpenPatientModal(true);
    setSelectedPatient(row);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (["address", "email", "name", "phone_number"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          [name]: value,
        },
      }));
    } else if (
      ["patient_name", "date_of_birth", "gender", "profile_pic_path"].includes(
        name,
      )
    ) {
      setFormData((prev) => ({
        ...prev,
        patient: {
          ...prev.patient,
          [name]: value,
        },
      }));
    }
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  /* ================================================================== */
  /* Derived / helper values (no API calls)                              */
  /* ================================================================== */

  const requiredUserFields = ["address", "email", "name", "phone_number"];
  const requiredPatientFields = ["patient_name", "date_of_birth", "gender"];

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    requiredUserFields.every((key) => formData.user[key]?.trim() !== "") &&
    requiredPatientFields.every((key) => formData.patient[key]?.trim() !== "");

  function getFilteredServicesByBranch(servicesData, branchId) {
    if (!Array.isArray(servicesData)) return [];
    return servicesData.filter((service) => service.branch_id === branchId);
  }

  const filteredRows = rows?.filter((row) =>
    Object.values(row).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  );

  const getBirthdayInfo = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) return null;
  
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    let nextBirthday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    nextBirthday.setHours(0, 0, 0, 0);
  
    if (nextBirthday < today) {
      nextBirthday = new Date(today.getFullYear() + 1, dob.getMonth(), dob.getDate());
    }
  
    const daysUntil = Math.round((nextBirthday - today) / (1000 * 60 * 60 * 24));
  
    if (daysUntil === 0) return { type: "today", date: nextBirthday };
    if (daysUntil >= 1 && daysUntil <= 6) return { type: "week", date: nextBirthday };
    return null;
  };

  /* ================================================================== */
  /* Patient CRUD handlers                                               */
  /* ================================================================== */

  const handleCreatePatient = async () => {
    if (!isFormValid) {
      setSnackbarOpen({
        status: true,
        message: "Please fill all required fields correctly.",
        severity: "error",
      });
      return;
    }
    if (isEditClicked) {
      sendPatientEdit();
    } else {
      try {
        const response = await api.post(`app/patient/${branchId}`, formData);
        if (response.status === 200) {
          setFormData({
            user: {
              address: "",
              email: "",
              name: "",
              phone_number: "",
            },
            patient: {
              patient_name: "",
              date_of_birth: null,
              gender: "",
              profile_pic_path: "",
            },
          });
          setSnackbarOpen({
            status: true,
            message: "Patient added successfully!",
            severity: "success",
          });
          setIsEditClicked(false);
          handleClose();
          getPatientDetails();
        }
      } catch (error) {
        setSnackbarOpen({
          status: true,
          message: error?.response?.data?.detail || "An error occurred",
          severity: "error",
        });
      }
    }
  };

  const getDetailsPatientWise = async (id) => {
    let patientIdData = await api.get(`app/patient/${id}`, {});
    const updatedData = {
      user: {
        address: patientIdData?.data.address,
        email: patientIdData?.data.email,
        name: patientIdData?.data.name,
        phone_number: patientIdData?.data.phone_number,
      },
      patient: {
        patient_name: patientIdData?.data.patient_name,
        date_of_birth: patientIdData?.data.date_of_birth,
        gender: patientIdData?.data.gender,
        profile_pic_path: "",
      },
    };
    setFormData(updatedData);
  };

  const sendPatientEdit = async () => {
    setIsEditClicked(true);
    try {
      let updatedPatientData = await api.put(
        `app/patient/${patientId}`,
        formData,
      );
      if (updatedPatientData.status === 200) {
        setFormData({
          user: {
            address: "",
            email: "",
            name: "",
            phone_number: "",
          },
          patient: {
            patient_name: "",
            date_of_birth: null,
            gender: "",
            profile_pic_path: "",
          },
        });
        setSnackbarOpen({
          status: true,
          message: "Patient Data Edited successfully !",
          severity: "success",
        });
        setIsEditClicked(false);
        handleClose();
        getPatientDetails();
      }
    } catch (error) {
      setSnackbarOpen({
        message: error?.response?.data?.detail || "An error occurred",
        severity: "error",
      });
    }
  };

  const handlePatientEdit = async (id) => {
    setPatientId(id);
    setIsEditClicked(true);
    await getDetailsPatientWise(id);
    handleOpen();
  };

  const handleDeletePatient = async (row) => {
    if (
      window.confirm(
        `Are you sure you want to delete patient ${row.patient_name}?`,
      )
    ) {
      try {
        let deleteResponse = await api.delete(`app/patient/${row.id}`);
        if (deleteResponse.status === 200) {
          setSnackbarOpen({
            status: true,
            message: "Patient deleted successfully!",
            severity: "success",
          });
          getPatientDetails();
        }
      } catch (error) {
        setSnackbarOpen({
          message: error?.response?.data?.detail || "An error occurred",
          severity: "error",
        });
      }
    }
  };

  const handleToggleIsActive = async (event, row) => {
    const newValue = event.target.checked;
    try {
      await api.put(`app/patient/${row.id}/`, {
        user: {},
        patient: { is_active: newValue },
      });
      if (typeof getPatientDetails === "function") {
        getPatientDetails();
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  /* ================================================================== */
  /* Therapy plan / slot assignment handlers                             */
  /* Create + update both hit the same endpoint: the backend upserts     */
  /* on (patient_id, service_id), so one function covers both cases.     */
  /* ================================================================== */

  const getServices = async () => {
    let servicesData = await api.get(`app/management/therapy-service/`);
    setServicesDetails(
      getFilteredServicesByBranch(servicesData?.data, branchId),
    );
  };

  const getTherapyPlans = async (id) => {
    try {
      const therapyData = await api.get(
        `app/patient/therapy/plans/?patient_id=${id}&branch_id=${branchId}`,
      );
      settherapyPlansData(therapyData?.data || []);
    } catch (error) {
      console.error("Error fetching therapy plans:", error);
    }
  };

  const handleClickOpen = async (id) => {
    await getServices();
    await getTherapyPlans(id);
    setDialogOpen(true);
    setPatientId(id);
    setPatientSlotData({});
  };

  const handleEditSlot = async (id) => {
    setPatientId(id);
    await getServices();
    await getTherapyPlans(id);
    setDialogOpen(true);
  };

  // Handles both create and update — the backend matches on
  // (patient_id, service_id) and upserts accordingly, so no separate
  // "edit" endpoint or recordID is needed here.
  const handleTherapyPlanSubmit = async (slotDataList) => {
    setSlotSubmitLoading(true);
    try {
      const response = await api.put(
        `app/patient/therapy/plans/${patientId}`,
        slotDataList,
      );
      if (response?.status === 200) {
        setSnackbarOpen({
          status: true,
          message:
            response?.data?.message || "Therapy plan saved successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbarOpen({
        severity: "error",
        status: true,
        message: "Something went wrong! Please try again.",
      });
    }
    await getTherapyPlans(patientId);
    getPatientDetails();
    setSlotSubmitLoading(false);
  };

  // NOTE: delete endpoint not yet confirmed against the backend route —
  // left unchanged from the original. Verify app/patient/therapy_plans/{recordID}
  // is correct before relying on this in production.
  const handlePatientSlotDelete = async (recordID) => {
    try {
      const response = await api.delete(
        `app/patient/therapy_plans/${recordID}`,
      );
      if (response?.status === 200) {
        setSnackbarOpen({
          status: true,
          message: response?.data?.message || "Service Deleted successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbarOpen({
        severity: "error",
        status: true,
        message: "Something went wrong! Please try again.",
      });
    }
    await getTherapyPlans(patientId);
    getPatientDetails();
  };

  /* ================================================================== */
  /* Invoice preview                                                      */
  /* All the old invoice dialog / publish / exclude-session flow is gone. */
  /* Clicking "Print invoice" now just opens the standalone preview page  */
  /* for the current month/year in a new tab — the page itself handles   */
  /* changing the period.                                                */
  /* ================================================================== */

  const handlePrintInvoicePreview = (patientRowId, branchIdForRow) => {
    const now = new Date();
    const url =
      APP_URL +
      `/invoice/preview?patient_id=${patientRowId}&branch_id=${branchIdForRow}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`;
    window.open(url, "_blank");
  };

  /* ================================================================== */
  /* DataGrid column definitions                                         */
  /* ================================================================== */

  const columns = [
    {
      field: "profile_pic_path",
      headerName: "",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      editable: false,
      renderCell: (params) => {
        const imagePath = params.row.profile_pic_path
          ? `${BASE_URL}${params.row.profile_pic_path}`
          : DEFAULT_IMAGE;
        const isDefault = !params.row.profile_pic_path;
        const birthdayInfo = getBirthdayInfo(params.row.date_of_birth);

        const handleImageUpload = async (event, patient_id) => {
          const file = event.target.files[0];
          if (!file) {
            setSnackbarOpen({
              status: true,
              message: "Please select an image file to upload.",
              severity: "error",
            });
            return;
          }
          const formData = new FormData();
          formData.append("file", file);
          try {
            let uploadImage = await api.post(
              `app/patient/add_patient_profile_pic/${patient_id}`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              },
            );
            if (uploadImage.status === 200) {
              setSnackbarOpen({
                status: true,
                message: "Profile picture uploaded successfully!",
              });
              getPatientDetails();
            }
          } catch (error) {
            setSnackbarOpen({
              message: error?.response?.data?.detail || "An error occurred",
              severity: "error",
            });
          }
          getPatientDetails();
        };
        const inputId = `upload-input-${params.row.id}`;
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              cursor: "pointer",
              position: "relative",
            }}
            onClick={() => {
              document.getElementById(inputId).click();
            }}
            title={isDefault ? "Click to upload image" : ""}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                padding: "2px",
                border: `2px solid ${palette.line}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "border-color 120ms ease",
                "&:hover": { borderColor: palette.slate },
              }}
            >
              <img
                src={imagePath}
                alt="Profile"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_IMAGE;
                }}
              />
              {birthdayInfo && (
  <Tooltip
    title={
      birthdayInfo.type === "today"
        ? "🎂 Birthday today!"
        : `🎂 Birthday this week — ${birthdayInfo.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    }
  >
    <Box
      sx={{
        position: "absolute",
        bottom: -2,
        right: -2,
        width: 18,
        height: 18,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: birthdayInfo.type === "today" ? palette.coral : "#D97706",
        border: "2px solid #fff",
      }}
    >
      {birthdayInfo.type === "today" ? (
        <CakeRoundedIcon sx={{ fontSize: 11, color: "#fff" }} />
      ) : (
        <CakeOutlinedIcon sx={{ fontSize: 11, color: "#fff" }} />
      )}
    </Box>
  </Tooltip>
)}
            </Box>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={(event) => handleImageUpload(event, params.row.id)}
            />
          </div>
        );
      },
    },
    {
      field: "patient_name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (params) => (
        <span
          style={{
            cursor: "pointer",
            fontWeight: 600,
            color: palette.ink,
          }}
          onClick={() => handleNameClick(params.row)}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "age",
      headerName: "Age",
      flex: 0.6,
      minWidth: 100,
      editable: false,
    },
    {
      field: "name",
      headerName: "Parent Name",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "phone_number",
      headerName: "Mobile",
      flex: 1,
      minWidth: 130,
      editable: false,
    },
    {
      field: "created_date",
      headerName: "Created Date",
      flex: 1,
      minWidth: 180,
      type: "dateTime",
      valueGetter: (params) => (params ? new Date(params) : null),
      valueFormatter: (params) => {
        if (!params) return "";
        const date = params;
        return date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      field: "is_active",
      headerName: "Is Active",
      flex: 0.8,
      minWidth: 120,
      editable: false,
      sortable: false,
      renderCell: (param) => (
        <Switch
          defaultChecked={param.row.is_active}
          onChange={(event) => handleToggleIsActive(event, param.row)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: palette.teal },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: palette.teal,
            },
          }}
        />
      ),
    },
    {
      field: "is_slot_allocated",
      headerName: "Is Slot Assigned",
      flex: 1.2,
      minWidth: 170,
      editable: false,
      sortable: false,
      renderCell: (param) => (
        <Box className="center-align">
          {param.row.is_slot_allocated ? (
            <Chip
              size="small"
              icon={
                <EventAvailableRoundedIcon
                  sx={{ fontSize: "15px !important", color: "#0B6B5F !important" }}
                />
              }
              label="See Slot Details"
              onClick={() => {
                handleEditSlot(param.row.id);
              }}
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#0B6B5F",
                backgroundColor: "transparent",
                border: `1px solid ${palette.teal}`,
                cursor: "pointer",
                "&:hover": { backgroundColor: palette.tealSoft },
              }}
            />
          ) : (
            <Switch
              defaultChecked={param.row.is_slot_allocated}
              onChange={(event) => {
                if (event.target.checked) {
                  handleClickOpen(param.row.id);
                }
              }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: palette.teal },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: palette.teal,
                },
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 140,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const isActive = params.row.is_active;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              height: "100%",
            }}
          >
            <Tooltip title={isActive ? "Print invoice" : "Patient is inactive"}>
              <Box
                sx={actionIconBtn(
                  isActive ? palette.slate : "#CBD5E1",
                  "#F1F5F9",
                )}
                style={{ pointerEvents: isActive ? "auto" : "none" }}
                onClick={() => {
                  if (isActive) {
                    handlePrintInvoicePreview(params.row.id, params.row.branch_id);
                  }
                }}
              >
                <PrintRoundedIcon sx={{ fontSize: "18px" }} />
              </Box>
            </Tooltip>
            <Tooltip title={isActive ? "Edit patient" : "Patient is inactive"}>
              <Box
                sx={actionIconBtn(
                  isActive ? palette.slate : "#CBD5E1",
                  "#F1F5F9",
                )}
                style={{ pointerEvents: isActive ? "auto" : "none" }}
                onClick={() => {
                  if (isActive) {
                    handlePatientEdit(params.row.id);
                  }
                }}
              >
                <BorderColorRoundedIcon sx={{ fontSize: "17px" }} />
              </Box>
            </Tooltip>
            {!flags.isReceptionist && (
              <Tooltip title="Delete patient">
                <Box
                  sx={actionIconBtn(palette.coral, palette.coralSoft)}
                  onClick={() => handleDeletePatient(params.row)}
                >
                  <DeleteRoundedIcon sx={{ fontSize: "17px" }} />
                </Box>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Box className="tabcontent_box" sx={{ backgroundColor: palette.bg }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            padding: "16px 20px",
            marginBottom: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Grid
            container
            spacing={2}
            className="patientTable-btnstack"
            display="flex"
            alignItems="center"
          >
            <Grid size={8} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button
                variant="contained"
                startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: "18px" }} />}
                sx={[
                  registerPatientButtonStyles,
                  addButtonPadding,
                  {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    backgroundColor: palette.indigo,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: palette.indigoDark,
                      boxShadow: "none",
                    },
                  },
                ]}
                size="small"
                onClick={handleOpen}
              >
                Add New Patient
              </Button>
              <span style={{ fontSize: "13px", color: palette.slate }}>
                {filteredRows?.length ?? 0} patient
                {filteredRows?.length === 1 ? "" : "s"}
              </span>
            </Grid>
            <Grid size={4} sx={{ textAlign: "right" }}>
              <TextField
                size="small"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: palette.bg,
                    fontSize: "13.5px",
                    "& fieldset": { borderColor: palette.line },
                    "&:hover fieldset": { borderColor: palette.slate },
                    "&.Mui-focused fieldset": { borderColor: palette.indigo },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: "18px", color: palette.slate }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            overflow: "hidden",
          }}
        >
          <DataGrid
            sx={{
              fontFamily: "Nunito, sans-serif",
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: palette.bg,
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "12.5px",
                fontWeight: 700,
                color: palette.slate,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13px",
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#F5F6F8",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${palette.line}`,
              },
            }}
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
          />
        </Paper>
      </Box>
      <PatientResistrationForm
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleCreatePatient={handleCreatePatient}
        isEditClicked={isEditClicked}
        validationErrors={validationErrors}
        isFormValid={isFormValid}
      />
      <SlotassignDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        handleSlotSubmit={handleTherapyPlanSubmit}
        initialData={patientSlotData}
        serviceDetails={serviceDetails}
        therapyPlansData={therapyPlansData}
        handlePatientSlotDelete={handlePatientSlotDelete}
        handlePatientSlotEdit={handleTherapyPlanSubmit}
        loading={slotSubmitLoading}
        branchId={branchId}
      />
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
      <PatientSessionModal
        open={openPatientModal}
        onClose={handleClosePatientModal}
        selectedPatient={selectedPatient}
      />
    </>
  );
}
